import { Ionicons } from "@expo/vector-icons";
import { Redirect, Tabs, usePathname } from "expo-router";
import React, { useEffect, useState } from "react";
import { useDailyBadges } from "@/hooks/useDailyBadges";
import { useTranslation } from "react-i18next";
import { ActivityIndicator, Image, View } from "react-native";

import { HapticTab } from "@/components/haptic-tab";
import { supabase } from "@/utils/supabase";

// Pastille dessinée sur l'icône (pas tabBarBadge) : sur Android, un badge
// natif à chaîne vide restait collé même après passage à `undefined`.
const TAB_DOT_STYLE = {
  position: "absolute" as const,
  top: 0,
  right: -2,
  width: 10,
  height: 10,
  borderRadius: 5,
  backgroundColor: "#E74C3C",
};

function TabIcon({
  showDot,
  children,
}: {
  showDot: boolean;
  children: React.ReactNode;
}) {
  return (
    <View style={{ width: 32, height: 32, alignItems: "center", justifyContent: "center" }}>
      {children}
      {showDot ? <View style={TAB_DOT_STYLE} /> : null}
    </View>
  );
}

export default function TabLayout() {
  const { t } = useTranslation();
  const [authState, setAuthState] = useState<"checking" | "authed" | "anon">("checking");
  const { challengePending, missionsPending, refetch: refetchBadges } = useDailyBadges();
  const pathname = usePathname();

  // Rafraîchit les pastilles à chaque navigation (retour de celebration,
  // passage d'un onglet à l'autre…) pour qu'elles disparaissent dès que
  // l'action/quiz du jour est fait.
  useEffect(() => {
    refetchBadges();
  }, [pathname, refetchBadges]);

  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!active) return;
      setAuthState(session ? "authed" : "anon");
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      setAuthState(session ? "authed" : "anon");
    });
    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (authState === "checking") {
    return (
      <View style={{ flex: 1, justifyContent: "center", alignItems: "center", backgroundColor: "#FBEFE3" }}>
        <ActivityIndicator size="large" color="#CD7728" />
      </View>
    );
  }
  if (authState === "anon") {
    return <Redirect href="/login" />;
  }

  return (
    <Tabs
      screenOptions={{
        tabBarActiveTintColor: "#733801",
        tabBarInactiveTintColor: "#D4D6DD",
        headerShown: false,
        tabBarButton: HapticTab,
        tabBarStyle: {
          backgroundColor: "#FFFFFF",
          borderTopWidth: 0,
          paddingTop: 24,
          paddingBottom: 24,
          height: 120,
          paddingRight: 24,
          paddingLeft: 24,
        },
        tabBarLabelStyle: {
          fontFamily: "Inter_400Regular",
          fontSize: 12,
          marginTop: 4,
        },
        tabBarIconStyle: {
          marginTop: 8,
        },
      }}
    >
      <Tabs.Screen
        name="index"
        options={{
          title: t("tabs.challenge"),
          tabBarIcon: ({ focused }) => (
            <TabIcon showDot={challengePending}>
              <Image
                source={require("../../assets/images/icon_logo.png")}
                style={{
                  width: 32,
                  height: 32,
                  tintColor: focused ? "#733801" : "#D4D6DD",
                }}
                resizeMode="contain"
              />
            </TabIcon>
          ),
        }}
      />
      <Tabs.Screen
        name="quiz"
        options={{
          title: t("tabs.missions"),
          tabBarIcon: ({ color }) => (
            <TabIcon showDot={missionsPending}>
              <Ionicons name="rocket" size={26} color={color} />
            </TabIcon>
          ),
        }}
      />
      <Tabs.Screen
        name="classement"
        options={{
          title: t("tabs.ranking"),
          tabBarIcon: ({ color }) => (
            <Ionicons name="trophy" size={26} color={color} />
          ),
        }}
      />
      <Tabs.Screen
        name="profil"
        options={{
          title: t("tabs.profile"),
          tabBarIcon: ({ color }) => (
            <Ionicons name="person" size={26} color={color} />
          ),
        }}
      />
    </Tabs>
  );
}
